import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Car,
    Clock,
    ExternalLink,
    FileText,
    Loader2,
    Scale,
    Shield,
    Users,
    Wrench,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { filoMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Section({ title, icon: Icon, children }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[#0B1220]/60 p-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                {title}
            </div>
            {children}
        </div>
    );
}

export default function FiloHasarDetayDrawer({ hasarId, onClose, onRefresh }) {
    const [loading, setLoading] = useState(true);
    const [detay, setDetay] = useState(null);

    const load = useCallback(async () => {
        if (!hasarId) return;
        setLoading(true);
        try {
            const data = await filoMerkez.hasarDetay(hasarId);
            setDetay(data.hasar || null);
        } catch {
            toast.error("Hasar detayı yüklenemedi.");
            setDetay(null);
        } finally {
            setLoading(false);
        }
    }, [hasarId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!hasarId) return null;

    const evrak = detay?.evrak_checklist;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <button
                type="button"
                className="absolute inset-0 bg-black/60"
                aria-label="Kapat"
                onClick={onClose}
            />
            <aside className="relative w-full max-w-xl h-full bg-[#121A2F] border-l border-white/10 shadow-2xl flex flex-col">
                <header className="flex items-center gap-3 p-4 border-b border-white/10 shrink-0">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase text-slate-500">Hasar detayı</div>
                        <div className="font-display text-lg text-white truncate">
                            {detay?.plaka || "—"} · {detay?.filo_adi || ""}
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose} aria-label="Kapat">
                        <X className="h-4 w-4" />
                    </Button>
                </header>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                        </div>
                    ) : !detay ? (
                        <p className="text-center text-slate-500 py-12">Kayıt bulunamadı.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="rounded-lg border border-white/10 p-2">
                                    <div className="text-slate-500">Durum</div>
                                    <div className="text-white mt-0.5">{detay.durum_label}</div>
                                </div>
                                <div className="rounded-lg border border-white/10 p-2">
                                    <div className="text-slate-500">Hasar tipi</div>
                                    <div className="text-white mt-0.5">{detay.hasar_tipi_label}</div>
                                </div>
                                <div className="rounded-lg border border-white/10 p-2 col-span-2">
                                    <div className="text-slate-500">Hukuk dosyası</div>
                                    {detay.dosya_no ? (
                                        <Link
                                            to={detay.dosya_link}
                                            className="text-[#D4AF37] hover:underline mt-0.5 inline-flex items-center gap-1"
                                        >
                                            {detay.dosya_no} — {detay.dosya_durumu}
                                            <ExternalLink className="h-3 w-3" />
                                        </Link>
                                    ) : (
                                        <span className="text-rose-400">Bağlı değil</span>
                                    )}
                                </div>
                            </div>

                            {evrak ? (
                                <Section title="Evrak checklist" icon={FileText}>
                                    <div className="text-[11px] text-slate-400 mb-2">
                                        Tamamlanma: %{evrak.tamamlanma_yuzde ?? 0}
                                        {evrak.tamam ? (
                                            <span className="text-emerald-400 ml-2">Tamam</span>
                                        ) : (
                                            <span className="text-amber-400 ml-2">Eksik var</span>
                                        )}
                                    </div>
                                    <ul className="space-y-1 text-xs">
                                        {(evrak.zorunlu || []).map((e) => (
                                            <li
                                                key={e.kod}
                                                className={
                                                    e.yuklendi ? "text-emerald-300" : "text-rose-300"
                                                }
                                            >
                                                {e.yuklendi ? "✓" : "○"} {e.label}
                                            </li>
                                        ))}
                                    </ul>
                                </Section>
                            ) : null}

                            {detay.ekspertiz ? (
                                <Section title="Ekspertiz" icon={Scale}>
                                    <div className="text-sm text-white">
                                        {detay.ekspertiz.eksper_adi || detay.ekspertiz.servis_adi || "—"}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1">
                                        {detay.ekspertiz.durum_label}
                                        {detay.ekspertiz.randevu_tarihi
                                            ? ` · Randevu: ${detay.ekspertiz.randevu_tarihi.slice(0, 16)}`
                                            : ""}
                                    </div>
                                    <Link
                                        to={`/e/ekspertiz-kayitlari/${detay.ekspertiz.id}`}
                                        className="text-[10px] text-[#D4AF37] hover:underline mt-2 inline-block"
                                    >
                                        Ekspertiz kaydını düzenle
                                    </Link>
                                </Section>
                            ) : null}

                            {detay.onarimlar?.length ? (
                                <Section title="Onarım" icon={Wrench}>
                                    <ul className="space-y-2 text-xs">
                                        {detay.onarimlar.map((o) => (
                                            <li key={o.id} className="border-b border-white/5 pb-2">
                                                <div className="text-white">{o.servis_adi}</div>
                                                <div className="text-slate-500">
                                                    {o.durum_label} · {o.tutar} ₺
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </Section>
                            ) : null}

                            {detay.karsi_taraflar?.length ? (
                                <Section title="Karşı taraf" icon={Users}>
                                    <ul className="space-y-2 text-xs">
                                        {detay.karsi_taraflar.map((k) => (
                                            <li key={k.id}>
                                                <span className="text-white">{k.plaka || k.ad_soyad}</span>
                                                {k.sigorta_sirketi ? (
                                                    <span className="text-slate-500">
                                                        {" "}
                                                        · {k.sigorta_sirketi}
                                                    </span>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </Section>
                            ) : null}

                            {detay.ic_hasar_gecmisi ? (
                                <Section title="İç hasar geçmişi" icon={Shield}>
                                    <p className="text-[11px] text-slate-500 mb-2">
                                        {detay.ic_hasar_gecmisi.aciklama}
                                    </p>
                                    <div className="text-sm text-white">
                                        {detay.ic_hasar_gecmisi.adet} kayıt ·{" "}
                                        {detay.ic_hasar_gecmisi.toplam_tutar} ₺
                                    </div>
                                </Section>
                            ) : null}

                            <Section title="Timeline" icon={Clock}>
                                <ul className="space-y-2 max-h-64 overflow-y-auto">
                                    {(detay.timeline || []).map((ev, i) => (
                                        <li
                                            key={`${ev.tip}-${ev.tarih}-${i}`}
                                            className="text-xs border-l-2 border-[#D4AF37]/40 pl-3 py-1"
                                        >
                                            <div className="text-white">{ev.baslik}</div>
                                            {ev.detay ? (
                                                <div className="text-slate-500">{ev.detay}</div>
                                            ) : null}
                                            <div className="text-[10px] text-slate-600">
                                                {ev.tarih?.slice(0, 16)}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        </>
                    )}
                </div>

                {detay ? (
                    <footer className="p-3 border-t border-white/10 flex gap-2 shrink-0">
                        <Link
                            to={`/e/hasar-kayitlari/${detay.id}`}
                            className="text-xs text-slate-400 hover:text-white"
                        >
                            CRUD
                        </Link>
                        <button
                            type="button"
                            className="text-xs text-[#D4AF37] hover:underline ml-auto"
                            onClick={() => {
                                load();
                                onRefresh?.();
                            }}
                        >
                            Yenile
                        </button>
                    </footer>
                ) : null}
            </aside>
        </div>
    );
}
